import {
  FirestoreCollectionProsessorOptions,
  createFirestoreCollectionProsessor,
} from "../firebase/firestore";

import "./View";
import "./Sheet";
import { DATETIME_WITH_SECONDS_FORMAT } from "@/config/date";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { OrganizationServiceType, optimizationText } from "./Optimize";

const versionOptimizeStatusesInProgress = [
  "queued",
  "queued_optimization",
  "in_progress_evaluation",
  "in_progress_optimization",
] as const;

const versionOptimizeStatusesFinished = [
  "success_evaluation",
  "success_optimization",
  "failure_in_optimization",
  "failure_input_evaluation",
  "failure_exception",
  "canceled_evaluation",
  "canceled_optimization",
] as const;

const versionOptimizeStatuses = [
  ...versionOptimizeStatusesInProgress,
  ...versionOptimizeStatusesFinished,
] as const;

export type VersionOptimizeStatus =
  | (typeof versionOptimizeStatuses)[number]
  | null;
type OptimizeStatusesText =
  | "処理待機中"
  | "評価実行中"
  | "評価成功"
  | "最適化待機中"
  | "最適化実行中"
  | "最適化成功"
  | "最適化失敗"
  | "入力検証失敗"
  | "エラー"
  | "評価キャンセル"
  | "最適化キャンセル";
type OptimizeStatusesTextForPlanium =
  | "処理待機中"
  | "評価実行中"
  | "評価成功"
  | "自動ドラフト待機中"
  | "自動ドラフト実行中"
  | "自動ドラフト成功"
  | "自動ドラフト失敗"
  | "入力検証失敗"
  | "エラー"
  | "評価キャンセル"
  | "自動ドラフトキャンセル";

type OptimizeSaveType = "overwrite_version" | "save_as_new_version" | null;

export type PublishedByOperationKinds =
  | "create"
  | "upload"
  | "evaluate"
  | "optimize"
  | "file_optimize"
  | "file_evaluate";
type PublishedByOperationKindsText =
  | "新規作成"
  | "ファイルアップロード"
  | "評価"
  | "最適化"
  | "ファイル最適化"
  | "ファイル評価";
type PublishedByOperationKindsTextForPlanium =
  | "新規作成"
  | "ファイルアップロード"
  | "評価"
  | "自動ドラフト"
  | "ファイル最適化"
  | "ファイル評価";

export const versionOperationStatuses = [
  "in_progress_remove",
  "in_progress_copy_from",
  "in_progress_copy_to",
  "in_progress_connect_result",
] as const;

type VersionOperationStatus = (typeof versionOperationStatuses)[number] | null;

const versionOperationStatusesInProgress = [
  "in_progress_remove",
  "in_progress_copy_from",
  "in_progress_copy_to",
  // NOTE: コネクトアドオンによる書き込み中はフロント操作が可能なため処理中扱いではない
  // "in_progress_connect_result",
] as const;

export type Version = {
  id: string;
  comment: string | null;
  version: string;
  title: string;
  createdAt: string;
  publishedAt: string;
  editedUsersUids: string[];
  isDisabled: boolean;
  // カテゴリのID
  category: string;
  lastModifiedAt: string;
  operationStatus: VersionOperationStatus;
  optimizeAlgorithmVersion: string | null;
  optimizeFinishedAt: string | null;
  optimizeFromVersion: string | null;
  // 古いバージョンはoptimizeJobDirのみを持っている場合がある
  // 現状optimizeJobWorkingDirとoptmizeJobDirは同値
  optimizeJobDir: null;
  optimizeJobWorkingDir: null;
  optimizeJobId: null;
  optimizeJobInputFilePath: string | null;
  optimizeScoreByUser: null;
  optimizeWeightType: null;
  optimizeStatus: VersionOptimizeStatus;
  optimizeSaveType: OptimizeSaveType;
  publishedByOperation: PublishedByOperationKinds;
  schemaVersion: string;
  // 未設定は空文字。サーバー側の表現に合わせている
  initialViewId: string;
};

type VersionRaw = {
  id: string;
  comment: string | null;
  version: string;
  title: string;
  created_at: string;
  published_at: string;
  edited_users_uids: string[];
  is_disabled: boolean;
  category: string;
  last_modified_at: string;
  operation_status: VersionOperationStatus;
  optimize_algorithm_version: string | null;
  optimize_finished_at: string | null;
  optimize_from_version: string | null;
  optimize_job_dir: null;
  optimize_job_working_dir: null;
  optimize_job_id: null;
  optimize_job_input_file_path: string | null;
  optimize_score_by_user: null;
  optimize_weight_type: null;
  optimize_status: VersionOptimizeStatus;
  optimize_save_type: OptimizeSaveType;
  published_by_operation: PublishedByOperationKinds;
  schema_version: string;
  initial_view_id: string;
};

// バージョンの操作を完全禁止（ビュー・シート非表示）する場合に表示するメッセージを返す
export const getLockOperationMessage = (
  version: Version | null,
  orgServiceType: OrganizationServiceType | null
): string => {
  if (!version) return "読み込み中です...";
  if (version.operationStatus === "in_progress_copy_to")
    return "このバージョンは現在複製中です...";
  if (version.operationStatus === "in_progress_remove")
    return "このバージョンは現在削除中です...";
  if (
    orgServiceType &&
    version.optimizeSaveType === "save_as_new_version" &&
    versionOptimizeStatusesInProgress.some((s) => s === version.optimizeStatus)
  ) {
    return orgServiceType === "planium"
      ? "このバージョンは現在自動ドラフトを実行中です..."
      : `このバージョンは現在評価・${optimizationText[orgServiceType]}を実行中です...`;
  }
  return "";
};

// バージョンの操作を禁止せず警告表示のみする場合のメッセージを返す
// NOTE: 複製元バージョン、上書き最適化中のバージョンについて本来は完全禁止したいが、UXを優先して警告表示のみとする
export const getAlertOperationMessage = (
  version: Version | null,
  orgServiceType: OrganizationServiceType | null
): string => {
  if (!version || getLockOperationMessage(version, orgServiceType)) return "";

  if (version.operationStatus === "in_progress_copy_from")
    return "このバージョンは現在複製中です。複製が完了するまでデータの編集をしないでください。";
  if (
    orgServiceType &&
    versionOptimizeStatusesInProgress.some((s) => s === version.optimizeStatus)
  )
    return orgServiceType === "planium"
      ? "このバージョンは現在自動ドラフトを実行中です。完了後、データが自動で上書きされます。"
      : `このバージョンは現在評価・${optimizationText[orgServiceType]}を実行中です。完了後、データが自動で上書きされます。`;

  return "";
};

// 複製可能かどうか
export const checkIsCopyable = (version: Version | null): boolean => {
  if (!version) return false;
  const isOperationStatusInProgress = versionOperationStatusesInProgress.some(
    (s) => s === version.operationStatus
  );
  const isOptimizeStatusInProgress =
    version.optimizeSaveType === "save_as_new_version"
      ? versionOptimizeStatusesInProgress.some(
          (s) => s === version.optimizeStatus
        )
      : // NOTE: 上書き最適化の場合はUXを優先して最適化ステータスが最適化中の時のみ複製をロックする
        version.optimizeStatus === "in_progress_optimization";
  return !isOperationStatusInProgress && !isOptimizeStatusInProgress;
};

// 完全削除可能かどうか
export const checkIsRemovable = (version: Version | null): boolean => {
  if (!version) return false;
  const isOperationStatusInProgress = versionOperationStatusesInProgress.some(
    (s) => s === version.operationStatus
  );
  const isOptimizeStatusInProgress = versionOptimizeStatusesInProgress.some(
    (s) => s === version.optimizeStatus
  );
  return !isOperationStatusInProgress && !isOptimizeStatusInProgress;
};

// 最適化可能かどうか
export const checkIsOptimizable = (version: Version | null) => {
  if (!version) return false;
  const isOperationStatusInProgress = versionOperationStatusesInProgress.some(
    (s) => s === version.operationStatus
  );
  const isOptimizeStatusInProgress = versionOptimizeStatusesInProgress.some(
    (s) => s === version.optimizeStatus
  );
  return !isOperationStatusInProgress && !isOptimizeStatusInProgress;
};

export const checkIsPublishedByFileUpload = (version: Version | null) => {
  if (!version) return false;
  const { publishedByOperation } = version;
  return (
    publishedByOperation === "file_optimize" ||
    publishedByOperation === "file_evaluate"
  );
};

export const checkIsEnableDownloadInput = (version: Version | null) => {
  if (!version) return false;
  const { optimizeJobInputFilePath } = version;
  return (
    typeof optimizeJobInputFilePath === "string" &&
    optimizeJobInputFilePath.length > 0
  );
};

export const checkIsEnableDownloadResult = (version: Version | null) => {
  if (!version) return false;
  const { optimizeStatus } = version;
  return (
    optimizeStatus === "success_evaluation" ||
    optimizeStatus === "success_optimization" ||
    optimizeStatus === "failure_input_evaluation" ||
    optimizeStatus === "failure_in_optimization" ||
    optimizeStatus === "failure_exception"
  );
};

export const checkIsEnableDownloadErrorFile = (version: Version | null) => {
  if (!version) return false;
  const { optimizeStatus } = version;
  return optimizeStatus === "failure_input_evaluation";
};

export const checkIsOptimizingFinished = (version: Version | null) => {
  if (!version) return false;
  const { optimizeStatus } = version;
  if (!optimizeStatus) return true;
  return (
    optimizeStatus === "success_evaluation" ||
    optimizeStatus === "success_optimization" ||
    optimizeStatus === "failure_in_optimization" ||
    optimizeStatus === "failure_input_evaluation" ||
    optimizeStatus === "failure_exception" ||
    optimizeStatus === "canceled_evaluation" ||
    optimizeStatus === "canceled_optimization"
  );
};

export const checkIsCancelable = (version: Version): boolean => {
  const { optimizeStatus } = version;
  if (!optimizeStatus) return false;
  return versionOptimizeStatusesInProgress.some(
    (s) => s === version.optimizeStatus
  );
};

export const extractExtensionForInputFile = (
  version: Version | null
): string | null => {
  if (!version || !version.optimizeJobInputFilePath) return null;
  const extM = version.optimizeJobInputFilePath.match(/\.(\w+)$/);
  if (!extM) return null;
  return extM[1];
};

const processorOptions: FirestoreCollectionProsessorOptions<
  Version,
  VersionRaw,
  Record<string, string>
> = {
  getCollectionPath: ({ organizationId }) =>
    `/app/spreadsheet_ui/v/1/organizations/${organizationId}/versions`,
  // 最適化・複製などのステータスはサーバーが更新するため、クライアントからは書き込まない
  updatableFields: [
    "title",
    "comment",
    "category",
    "is_disabled",
    "initial_view_id",
    "last_modified_at",
  ],
  toFirestore: ({
    id,
    comment,
    version,
    title,
    createdAt,
    publishedAt,
    editedUsersUids,
    isDisabled,
    category,
    lastModifiedAt,
    operationStatus,
    optimizeAlgorithmVersion,
    optimizeFinishedAt,
    optimizeFromVersion,
    optimizeJobDir,
    optimizeJobWorkingDir,
    optimizeJobInputFilePath,
    optimizeJobId,
    optimizeScoreByUser,
    optimizeWeightType,
    optimizeStatus,
    optimizeSaveType,
    publishedByOperation,
    schemaVersion,
    initialViewId,
  }) => ({
    id,
    comment,
    version,
    title,
    created_at: createdAt,
    published_at: publishedAt,
    edited_users_uids: editedUsersUids,
    is_disabled: isDisabled,
    category,
    last_modified_at: format(new Date(), DATETIME_WITH_SECONDS_FORMAT, {
      locale: ja,
    }),
    // 古いバージョンはoperation_statusを持っていない場合があるためケア
    operation_status: operationStatus ?? null,
    optimize_algorithm_version: optimizeAlgorithmVersion,
    optimize_finished_at: optimizeFinishedAt,
    // 古いバージョンはoptimizeFromVersionを持っていない場合があるためケア
    optimize_from_version: optimizeFromVersion ?? null,
    optimize_job_dir: optimizeJobDir,
    // 古いバージョンはoptimizeJobWorkingDirを持っていない場合がある
    // その場合、optimizeJobDirが存在すればそこからコピーする
    optimize_job_working_dir: optimizeJobWorkingDir ?? optimizeJobDir ?? null,
    optimize_job_id: optimizeJobId,
    // 古いバージョンはoptimizeJobInputFilePathを持っていない場合があるためケア
    optimize_job_input_file_path: optimizeJobInputFilePath ?? null,
    optimize_score_by_user: optimizeScoreByUser,
    optimize_weight_type: optimizeWeightType,
    optimize_status: optimizeStatus,
    optimize_save_type: optimizeSaveType,
    published_by_operation: publishedByOperation,
    schema_version: schemaVersion,
    initial_view_id: initialViewId,
  }),
  fromFirestore: ({
    id,
    comment,
    version,
    title,
    created_at,
    published_at,
    edited_users_uids,
    is_disabled,
    category,
    last_modified_at,
    operation_status,
    optimize_algorithm_version,
    optimize_finished_at,
    optimize_from_version,
    optimize_job_dir,
    optimize_job_working_dir,
    optimize_job_id,
    optimize_job_input_file_path,
    optimize_score_by_user,
    optimize_weight_type,
    optimize_status,
    optimize_save_type,
    published_by_operation,
    schema_version,
    initial_view_id,
  }) => ({
    id,
    comment,
    version,
    title,
    createdAt: created_at,
    publishedAt: published_at,
    editedUsersUids: edited_users_uids,
    isDisabled: is_disabled,
    category,
    lastModifiedAt: last_modified_at,
    operationStatus: operation_status,
    optimizeAlgorithmVersion: optimize_algorithm_version,
    optimizeFinishedAt: optimize_finished_at,
    optimizeFromVersion: optimize_from_version,
    optimizeJobDir: optimize_job_dir,
    optimizeJobWorkingDir: optimize_job_working_dir,
    optimizeJobId: optimize_job_id,
    optimizeJobInputFilePath: optimize_job_input_file_path,
    optimizeScoreByUser: optimize_score_by_user,
    optimizeWeightType: optimize_weight_type,
    optimizeStatus: optimize_status,
    optimizeSaveType: optimize_save_type,
    publishedByOperation: published_by_operation,
    schemaVersion: schema_version,
    initialViewId: initial_view_id,
  }),
};

export const {
  useGet: useGetVersion,
  useSet: useSetVersion,
  useUpdate: useUpdateVersion,
  useRecursiveDelete: useRecursiveDeleteVersion,
  useItem: useVersion,
  useItemsQuery: useVersionsQuery,
} = createFirestoreCollectionProsessor(processorOptions);
